// Angular
import { Injectable } from '@angular/core';
// RxJS
import { BehaviorSubject } from 'rxjs';
// Object path
import * as objectPath from 'object-path';
// Services
import { MenuConfigService } from './menu-config.service';
import { MenuService } from './menu.service';
import { HEC_CONSTANTS } from '../../../../utilities/constants';

@Injectable()
export class MenuAsideService {
	// Public properties
	menuList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

	// Storage key for cached menu response
	private readonly MENU_STORAGE_KEY = 'esp-menu-list';

	/**
	 * Service constructor
	 *
	 * @param menuConfigService: MenuConfigService
	 * @param menuService: MenuService
	 */
	constructor(
		private menuConfigService: MenuConfigService,
		private menuService: MenuService
	) {
		this.loadMenu();
	}

	/**
	 * Load menu list from backend API with localStorage cache and static config fallback
	 */
	loadMenu() {
		// show cached menu immediately (avoids blank sidebar while API loads)
		const cached = this.getMenuFromStorage();
		if (cached && cached.length) {
			this.setMenuItems(cached);
		}

		this.menuService.getMenuList().subscribe(
			(res) => {
				if (this.isSuccessResponse(res) && Array.isArray(res.data)) {
					const menuItems = this.deduplicateMenuItems(
						res.data.map((item) => this.mapMenuItem(item))
					);
					this.saveMenuToStorage(menuItems);
					this.setMenuItems(menuItems);
				} else if (!cached || !cached.length) {
					this.loadMenuFromConfig();
				}
			},
			() => {
				if (!cached || !cached.length) {
					this.loadMenuFromConfig();
				}
			}
		);
	}

	private loadMenuFromConfig() {
		const menuItems: any[] = objectPath.get(this.menuConfigService.getMenus(), 'aside.items') || [];
		this.setMenuItems(menuItems);
	}

	private getMenuFromStorage(): any[] {
		try {
			const raw = localStorage.getItem(this.MENU_STORAGE_KEY);
			if (!raw) {
				return [];
			}
			const parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? parsed : [];
		} catch (e) {
			return [];
		}
	}

	private saveMenuToStorage(menuItems: any[]) {
		try {
			localStorage.setItem(this.MENU_STORAGE_KEY, JSON.stringify(menuItems));
		} catch (e) {
			// ignore storage errors (e.g. quota / private mode)
		}
	}

	private setMenuItems(menuItems: any[]) {
		const items = (menuItems || []).map((item) => this.withWheelchairMenuIcon(item));
		this.menuList$.next(items);
		this.menuConfigService.updateAsideItems(items);
	}

	private mapMenuItem(item: any): any {
		return this.withWheelchairMenuIcon({
			id: item.id,
			title: item.title,
			root: item.root,
			alignment: item.alignment,
			page: item.page,
			translate: item.translate,
			pageCode: item.pageCode,
			icon: item.icon,
			bullet: item.bullet,
			allwoed: item.allowed,
			allowed: item.allowed,
		});
	}

	private withWheelchairMenuIcon(item: any): any {
		if (!this.isWheelchairMenuItem(item) || !item) {
			return item;
		}
		return { ...item, icon: 'fas fa-wheelchair' };
	}

	private isWheelchairMenuItem(item: any): boolean {
		if (!item) {
			return false;
		}
		const page = item.page ? String(item.page).toLowerCase() : '';
		const title = item.title ? String(item.title).toLowerCase() : '';
		return (
			page.indexOf('/wcp') !== -1 ||
			page.indexOf('/pmws') !== -1 ||
			item.pageCode === 'WCP' ||
			item.pageCode === 'PMWS' ||
			title.indexOf('wheel chair') !== -1 ||
			title.indexOf('wheelchair') !== -1
		);
	}

	private deduplicateMenuItems(items: any[]): any[] {
		const seen = new Set<string>();
		return items.filter((item) => {
			const key = item.page;
			if (!key || seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});
	}

	private isSuccessResponse(res: any): boolean {
		return res && (res.statusCode == HEC_CONSTANTS.HTTP_RESPONSE.OK || res.statusCode == '200');
	}
}
